import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Clock, Calendar, User, AlertTriangle, CheckCircle, XCircle, Plus, Trash2,
  Edit, Save, Shield, FileWarning, CalendarOff, CalendarCheck, Users
} from 'lucide-react';
import { format, addDays, startOfWeek, parseISO } from 'date-fns';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday', short: 'Sun' },
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' },
];

const TIME_SLOTS = Array.from({ length: 24 }, (_, i) => {
  const h = i.toString().padStart(2, '0');
  return { value: `${h}:00`, label: `${h}:00` };
});

function TimeRangeInput({ range, onChange, onRemove }) {
  return (
    <div className="flex items-center gap-2">
      <Select value={range.start_time} onValueChange={(v) => onChange({ ...range, start_time: v })}>
        <SelectTrigger className="w-24">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {TIME_SLOTS.map(t => (
            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-muted-foreground">to</span>
      <Select value={range.end_time} onValueChange={(v) => onChange({ ...range, end_time: v })}>
        <SelectTrigger className="w-24">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {TIME_SLOTS.map(t => (
            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button size="sm" variant="ghost" onClick={onRemove} className="text-red-500">
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
}

function WeeklyAvailabilityEditor({ weekly, onChange }) {
  const getDayRanges = (dow) => {
    const day = weekly.find(w => w.day_of_week === dow);
    return day?.ranges || [];
  };

  const setDayRanges = (dow, ranges) => {
    const existing = weekly.find(w => w.day_of_week === dow);
    if (existing) {
      onChange(weekly.map(w => w.day_of_week === dow ? { ...w, ranges } : w));
    } else {
      onChange([...weekly, { day_of_week: dow, ranges }]);
    }
  };

  const addRange = (dow) => {
    const ranges = getDayRanges(dow);
    setDayRanges(dow, [...ranges, { start_time: '09:00', end_time: '17:00' }]);
  };

  const updateRange = (dow, index, newRange) => {
    const ranges = getDayRanges(dow);
    const updated = [...ranges];
    updated[index] = newRange;
    setDayRanges(dow, updated);
  };

  const removeRange = (dow, index) => {
    const ranges = getDayRanges(dow);
    setDayRanges(dow, ranges.filter((_, i) => i !== index));
  };

  const copyToAll = (dow) => {
    const ranges = getDayRanges(dow);
    if (ranges.length === 0) return;
    const newWeekly = DAYS_OF_WEEK.map(d => ({
      day_of_week: d.value,
      ranges: [...ranges.map(r => ({ ...r }))]
    }));
    onChange(newWeekly);
    toast.success('Copied to all days');
  };

  return (
    <div className="space-y-4">
      {DAYS_OF_WEEK.map(day => {
        const ranges = getDayRanges(day.value);
        const isAvailable = ranges.length > 0;
        
        return (
          <div key={day.value} className={`border rounded-lg p-3 ${isAvailable ? 'bg-green-50/50 border-green-200' : 'bg-gray-50/50'}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Badge variant={isAvailable ? 'default' : 'secondary'} className="w-20 justify-center">
                  {day.short}
                </Badge>
                {isAvailable ? (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                ) : (
                  <XCircle className="w-4 h-4 text-gray-400" />
                )}
              </div>
              <div className="flex items-center gap-2">
                {isAvailable && (
                  <Button size="sm" variant="ghost" onClick={() => copyToAll(day.value)} className="text-xs">
                    Copy to all
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => addRange(day.value)}>
                  <Plus className="w-3 h-3 mr-1" />
                  Add Time
                </Button>
              </div>
            </div>
            {ranges.length > 0 ? (
              <div className="space-y-2 ml-4">
                {ranges.map((r, i) => (
                  <TimeRangeInput
                    key={i}
                    range={r}
                    onChange={(nr) => updateRange(day.value, i, nr)}
                    onRemove={() => removeRange(day.value, i)}
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground ml-4">Not available</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ExceptionsEditor({ exceptions, onChange }) {
  const addException = () => {
    onChange([...exceptions, {
      date: format(new Date(), 'yyyy-MM-dd'),
      type: 'UNAVAILABLE',
      ranges: [],
      notes: ''
    }]);
  };

  const updateException = (index, updates) => {
    const updated = [...exceptions];
    updated[index] = { ...updated[index], ...updates };
    onChange(updated);
  };

  const removeException = (index) => {
    onChange(exceptions.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Add specific dates when availability differs from the weekly pattern</p>
        <Button onClick={addException}>
          <Plus className="w-4 h-4 mr-1" />
          Add Exception
        </Button>
      </div>

      {exceptions.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <CalendarOff className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>No exceptions added</p>
        </div>
      ) : (
        <div className="space-y-3">
          {exceptions.map((exc, i) => (
            <Card key={i} className={exc.type === 'UNAVAILABLE' ? 'border-red-200 bg-red-50/30' : 'border-green-200 bg-green-50/30'}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-3 flex-1">
                    <div className="flex items-center gap-4">
                      <div>
                        <Label className="text-xs">Date</Label>
                        <Input
                          type="date"
                          value={exc.date}
                          onChange={(e) => updateException(i, { date: e.target.value })}
                          className="w-40"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Type</Label>
                        <Select value={exc.type} onValueChange={(v) => updateException(i, { type: v })}>
                          <SelectTrigger className="w-44">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="UNAVAILABLE">
                              <span className="flex items-center gap-1"><XCircle className="w-3 h-3 text-red-500" /> Unavailable</span>
                            </SelectItem>
                            <SelectItem value="AVAILABLE_OVERRIDE">
                              <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-500" /> Available (Override)</span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Notes</Label>
                      <Input
                        value={exc.notes || ''}
                        onChange={(e) => updateException(i, { notes: e.target.value })}
                        placeholder="e.g., Doctor appointment, Personal leave"
                      />
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => removeException(i)} className="text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function ConflictChecker({ staffId, staffName }) {
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: format(new Date(), 'yyyy-MM-dd'),
    end: format(addDays(new Date(), 14), 'yyyy-MM-dd')
  });

  const checkConflicts = async () => {
    setLoading(true);
    try {
      // Get shifts for the date range
      const shiftsRes = await api.get(`/roster/shifts?start=${dateRange.start}&end=${dateRange.end}`);
      const shifts = shiftsRes.data.items || shiftsRes.data.data || [];
      
      // Filter shifts assigned to this staff
      const staffShifts = shifts.filter(s => 
        s.assigned_staff_id === staffId || 
        (s.assigned_staff_ids && s.assigned_staff_ids.includes(staffId))
      );

      // Check each shift for conflicts
      const foundConflicts = [];
      for (const shift of staffShifts) {
        try {
          const res = await api.get(`/roster/staff/${staffId}/availability-conflicts`, {
            params: {
              shift_date: shift.date,
              start_time: shift.start_time,
              end_time: shift.end_time
            }
          });
          if (res.data.data?.conflicts?.length > 0) {
            foundConflicts.push({
              shift,
              conflicts: res.data.data.conflicts
            });
          }
        } catch (e) {
          console.error('Conflict check failed for shift', shift.id, e);
        }
      }
      
      setConflicts(foundConflicts);
      if (foundConflicts.length === 0) {
        toast.success('No conflicts found');
      }
    } catch (e) {
      toast.error('Failed to check conflicts');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div>
          <Label className="text-xs">From</Label>
          <Input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
            className="w-40"
          />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
            className="w-40"
          />
        </div>
        <div className="pt-5">
          <Button onClick={checkConflicts} disabled={loading}>
            {loading ? 'Checking...' : 'Check Conflicts'}
          </Button>
        </div>
      </div>

      {conflicts.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-semibold text-red-600 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {conflicts.length} Conflict(s) Found
          </h4>
          {conflicts.map((c, i) => (
            <Card key={i} className="border-red-200 bg-red-50/30">
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{c.shift.title || 'Shift'}</p>
                    <p className="text-sm text-muted-foreground">
                      {c.shift.date} {c.shift.start_time}-{c.shift.end_time}
                    </p>
                  </div>
                  <div className="text-right">
                    {c.conflicts.map((conf, j) => (
                      <Badge key={j} variant={conf.severity === 'HIGH' ? 'destructive' : 'secondary'} className="ml-1">
                        {conf.type}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="mt-2 text-sm">
                  {c.conflicts.map((conf, j) => (
                    <p key={j} className="text-red-600">{conf.message}</p>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {conflicts.length === 0 && !loading && (
        <div className="text-center py-4 text-muted-foreground">
          <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
          <p>Click "Check Conflicts" to scan for scheduling issues</p>
        </div>
      )}
    </div>
  );
}

function ComplianceStatus({ staffId }) {
  const [compliance, setCompliance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/staff/${staffId}`)
      .then(res => setCompliance(res.data.data || res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [staffId]);

  if (loading) return <div className="text-sm text-muted-foreground">Loading compliance...</div>;
  if (!compliance) return null;

  const status = compliance.compliance_status || 'UNKNOWN';
  const statusConfig = {
    COMPLIANT: { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle, label: 'Compliant' },
    NON_COMPLIANT: { color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle, label: 'Non-Compliant' },
    EXPIRING_SOON: { color: 'bg-amber-100 text-amber-800 border-amber-200', icon: AlertTriangle, label: 'Expiring Soon' },
    UNKNOWN: { color: 'bg-gray-100 text-gray-800 border-gray-200', icon: FileWarning, label: 'Unknown' },
  };

  const config = statusConfig[status] || statusConfig.UNKNOWN;
  const Icon = config.icon;

  return (
    <Card className={`border ${config.color}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <Icon className="w-6 h-6" />
          <div>
            <p className="font-semibold">{config.label}</p>
            <p className="text-xs opacity-80">
              {status === 'NON_COMPLIANT' && 'Cannot be assigned to shifts until compliance is resolved'}
              {status === 'EXPIRING_SOON' && 'Some credentials are expiring - review soon'}
              {status === 'COMPLIANT' && 'All credentials are current and valid'}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StaffAvailabilityEditor({ staff, open, onClose, onSave }) {
  const [availability, setAvailability] = useState({ weekly: [], exceptions: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && staff) {
      setLoading(true);
      api.get(`/availability/staff/${staff.id}`)
        .then(res => {
          const data = res.data.data || {};
          setAvailability({
            weekly: data.weekly || [],
            exceptions: data.exceptions || []
          });
        })
        .catch(() => toast.error('Failed to load availability'))
        .finally(() => setLoading(false));
    }
  }, [open, staff]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/availability/staff/${staff.id}`, availability);
      toast.success('Availability saved');
      onSave();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!staff) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-500" />
            Availability: {staff.full_name}
          </DialogTitle>
          <DialogDescription>
            Manage weekly availability pattern and specific date exceptions
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-muted-foreground">Loading...</div>
        ) : (
          <Tabs defaultValue="weekly" className="mt-4">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="weekly">Weekly Pattern</TabsTrigger>
              <TabsTrigger value="exceptions">Exceptions</TabsTrigger>
              <TabsTrigger value="conflicts">Conflicts</TabsTrigger>
              <TabsTrigger value="compliance">Compliance</TabsTrigger>
            </TabsList>
            
            <TabsContent value="weekly" className="mt-4">
              <WeeklyAvailabilityEditor
                weekly={availability.weekly}
                onChange={(w) => setAvailability({ ...availability, weekly: w })}
              />
            </TabsContent>
            
            <TabsContent value="exceptions" className="mt-4">
              <ExceptionsEditor
                exceptions={availability.exceptions}
                onChange={(e) => setAvailability({ ...availability, exceptions: e })}
              />
            </TabsContent>
            
            <TabsContent value="conflicts" className="mt-4">
              <ConflictChecker staffId={staff.id} staffName={staff.full_name} />
            </TabsContent>
            
            <TabsContent value="compliance" className="mt-4">
              <ComplianceStatus staffId={staff.id} />
            </TabsContent>
          </Tabs>
        )}

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || loading}>
            <Save className="w-4 h-4 mr-1" />
            {saving ? 'Saving...' : 'Save Availability'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function StaffAvailabilityPage() {
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, availRes] = await Promise.all([
        api.get('/admin/users'),
        api.get('/staff').catch(() => ({ data: { data: [] } }))
      ]);
      
      const users = (usersRes.data.data || []).filter(u => 
        u.role_name === 'Support Worker' || u.role_name === 'Staff'
      );
      
      const staffData = availRes.data.data || [];
      
      // Merge user and staff data
      const merged = users.map(u => {
        const staff = staffData.find(s => s.user_id === u.id);
        return {
          ...u,
          compliance_status: staff?.compliance_status || 'UNKNOWN',
          has_availability: false // Will be checked individually
        };
      });
      
      setStaffList(merged);
    } catch (e) {
      toast.error('Failed to load staff');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = staffList.filter(s => 
    s.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getComplianceBadge = (status) => {
    const config = {
      COMPLIANT: { color: 'bg-green-100 text-green-700', label: 'Compliant' },
      NON_COMPLIANT: { color: 'bg-red-100 text-red-700', label: 'Non-Compliant' },
      EXPIRING_SOON: { color: 'bg-amber-100 text-amber-700', label: 'Expiring' },
      UNKNOWN: { color: 'bg-gray-100 text-gray-600', label: 'Unknown' },
    };
    const c = config[status] || config.UNKNOWN;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="staff-availability-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Calendar className="w-6 h-6 text-blue-500" />
            Staff Availability
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage staff availability patterns and check for scheduling conflicts
          </p>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <Input
          placeholder="Search staff..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-sm"
          data-testid="staff-search"
        />
        <span className="text-sm text-muted-foreground">{filtered.length} staff</span>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading staff...</div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">No staff found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filtered.map(s => (
            <Card key={s.id} className="hover:shadow-md transition-shadow" data-testid={`staff-card-${s.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <User className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold">{s.full_name}</p>
                      <p className="text-sm text-muted-foreground">{s.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getComplianceBadge(s.compliance_status)}
                    <Button onClick={() => setSelectedStaff(s)} data-testid={`edit-availability-${s.id}`}>
                      <Calendar className="w-4 h-4 mr-1" />
                      Manage Availability
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <StaffAvailabilityEditor
        staff={selectedStaff}
        open={!!selectedStaff}
        onClose={() => setSelectedStaff(null)}
        onSave={fetchData}
      />
    </div>
  );
}
